import { Add, Cancel, Close } from "@mui/icons-material";
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ChangeEvent,
  FormEvent,
  SyntheticEvent,
  useCallback,
  useState,
} from "react";
import {
  useCreateProductCategoryMutation,
  useGetProductCategoriesQuery,
} from "src/api/services/productCategoriesApi";
import { useCreateProductMutation } from "src/api/services/productsApi";
import {
  BillingModel,
  BillingModelEnums,
  ProductCategory,
} from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { desktopSx } from "src/utils/styles/modalStyles";

interface Props {
  open: boolean;
  onClose: () => void;
}

const CreateProductModal = ({ onClose, open }: Props) => {
  const [createProduct] = useCreateProductMutation();
  const [createCategory] = useCreateProductCategoryMutation();
  const { data: categories = [] } = useGetProductCategoriesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [category, setCategory] = useState<ProductCategory | null>(null);
  const [productName, setProductName] = useState<string>("");
  const [displayName, setDisplayName] = useState<string>("");
  const [slug, setSlug] = useState<string>("");
  const [billingModel, setBillingModel] = useState<BillingModelEnums | null>(
    null,
  );
  const [displayInput, setDisplayInput] = useState<boolean>(false);
  const [gender, setGender] = useState<string | null>(null);
  const [isGlp1, setIsGlp1] = useState<boolean>(false);
  const [insuranceBased, setInsuranceBased] = useState<boolean>(false);
  const [newCategory, setNewCategory] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const errorLog = useSentryLogger("error");

  const handleCategory = useCallback(
    (_: SyntheticEvent<Element | Event>, value: ProductCategory | null) => {
      setCategory(value);
    },
    [],
  );

  const handleProductName = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setProductName(e.target.value);
  }, []);

  const handleDisplayName = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setDisplayName(e.target.value);
  }, []);

  const handleSlug = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSlug(e.target.value);
  }, []);

  const handleBillingModel = useCallback(
    (_: SyntheticEvent<Element | Event>, value: BillingModelEnums | null) => {
      setBillingModel(value);
    },
    [],
  );

  const handleGender = useCallback(
    (_: SyntheticEvent<Element | Event>, value: string | null) => {
      if (value === "Unisex") {
        setGender(null);
        return;
      }

      setGender(value);
    },
    [],
  );

  const handleIsGlp1 = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setIsGlp1(e.target.checked);
  }, []);

  const handleInsuranceBased = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setInsuranceBased(e.target.checked);
    },
    [],
  );

  const handleNewCategory = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setNewCategory(e.target.value);
  }, []);

  const handleSubmitCategory = useCallback(async () => {
    if (newCategory.length === 0) return;

    try {
      const res = await createCategory({ name: newCategory }).unwrap();

      if (res != null) {
        setCategory(res);
        setDisplayInput(false);
      }
    } catch (err) {
      errorLog("Could not create new category", err);
    }
  }, [createCategory, errorLog, newCategory]);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      setLoading(true);

      if (category == null) return;
      if (billingModel == null) return;

      try {
        await createProduct({
          category_id: category.id,
          name: productName,
          slug,
          billing_model: billingModel,
          gender,
          display_name: displayName.length > 0 ? displayName : productName,
          insurance_based: insuranceBased,
          is_glp1: isGlp1,
        }).unwrap();
      } catch (err) {
        errorLog("Error creating product", err);
      } finally {
        setCategory(null);
        setProductName("");
        setDisplayName("");
        setSlug("");
        setBillingModel(null);
        setGender("");
        setIsGlp1(false);
        setLoading(false);
        onClose();
      }
    },
    [
      billingModel,
      category,
      createProduct,
      displayName,
      errorLog,
      gender,
      insuranceBased,
      isGlp1,
      onClose,
      productName,
      slug,
    ],
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Stack
        sx={desktopSx}
        gap={2}
        component="form"
        maxWidth={400}
        onSubmit={handleSubmit}
      >
        <Stack
          direction={"row"}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h5" component="h5" fontWeight={700}>
            Add Product
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
        <Stack gap={1}>
          <Autocomplete
            options={categories}
            getOptionLabel={(p) => p.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category"
                required
                helperText={
                  <Typography variant="caption" fontSize={"0.8rem"}>
                    Can't find the desired category.{" "}
                    <a
                      style={{ cursor: "pointer" }}
                      onClick={() => setDisplayInput(true)}
                    >
                      Add here
                    </a>
                  </Typography>
                }
              />
            )}
            value={category}
            onChange={handleCategory}
          />
          {displayInput ? (
            <Stack direction={"row"}>
              <TextField
                fullWidth
                label="New category"
                required
                value={newCategory}
                onChange={handleNewCategory}
              />
              <IconButton onClick={handleSubmitCategory}>
                <Add />
              </IconButton>
              <IconButton onClick={() => setDisplayInput(false)}>
                <Cancel />
              </IconButton>
            </Stack>
          ) : null}
          <TextField
            label="Name"
            required
            value={productName}
            onChange={handleProductName}
          />
          <TextField
            label="Display name"
            value={displayName}
            onChange={handleDisplayName}
          />
          <TextField
            label="Slug"
            helperText='One-time product slugs should end with "-ot"'
            required
            value={slug}
            onChange={handleSlug}
          />
          <Autocomplete
            options={BillingModel}
            getOptionLabel={(p) => p}
            renderInput={(params) => (
              <TextField {...params} label="Billing model" required />
            )}
            value={billingModel}
            onChange={handleBillingModel}
          />
          <Autocomplete
            options={["Unisex", "Male", "Female"]}
            getOptionLabel={(p) => p}
            renderInput={(params) => <TextField {...params} label="Gender" />}
            value={gender}
            onChange={handleGender}
          />
          <FormControlLabel
            sx={{
              alignItems: "center",
              gap: 1,
              margin: 0,
              color: "#6C7278",
              "& .MuiFormControlLabel-label": {
                fontSize: "14px",
              },
            }}
            control={
              <Checkbox
                checked={isGlp1}
                onChange={handleIsGlp1}
                sx={{
                  padding: 0,
                  "&.Mui-checked": {
                    color: "#FF8175",
                  },
                }}
              />
            }
            label="Is GLP-1"
          />
          <FormControlLabel
            sx={{
              alignItems: "center",
              gap: 1,
              margin: 0,
              color: "#6C7278",
              "& .MuiFormControlLabel-label": {
                fontSize: "14px",
              },
            }}
            control={
              <Checkbox
                checked={insuranceBased}
                onChange={handleInsuranceBased}
                sx={{
                  padding: 0,
                  "&.Mui-checked": {
                    color: "#FF8175",
                  },
                }}
              />
            }
            label="Insurance Based"
          />
          <LoadingButton type="submit" loading={loading}>
            Submit product
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default CreateProductModal;
